/**
 * HistoriqueAchats v2 — Vue historique dense alignée sur PurchasesList v2.
 *
 * Objectif : offrir la même expérience premium que la liste courante mais
 * orientée reporting / expert-comptable :
 *  - KPIs cliquables (Total, Payées, Encours, En retard)
 *  - Recherche + période 12m par défaut + filtres statut/source
 *  - Table dense enrichie avec badges Source/Statut, tri, pagination serveur
 *  - Export Excel + Archive ZIP (envoi cabinet)
 *  - Preview PDF au survol
 *
 * Utilise le même endpoint `/supplier-invoices/list-enriched` que la liste
 * courante — cohérence des KPIs entre onglets.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Search, Download, FileSpreadsheet, ArrowUpRight, Wallet, CheckCircle2,
  AlertTriangle, FileText, ArrowUp, ArrowDown, ChevronDown, CalendarDays,
  Plug, ScanLine, User, Repeat, Upload as UploadIcon,
} from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { cn } from "@/lib/utils";
import PdfPreviewPopover from "@/components/PdfPreviewPopover";

const eur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR",
  { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const eurCompact = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} M€`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(1)} k€`;
  return `${v.toFixed(0)} €`;
};
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR") : "—";

const STATUS_META = {
  to_validate:    { label: "À valider",    cls: "bg-amber-50 text-amber-800 border-amber-200" },
  validated:      { label: "Validée",      cls: "bg-blue-50 text-blue-700 border-blue-200" },
  imported:       { label: "Importée",     cls: "bg-sky-50 text-sky-700 border-sky-200" },
  partially_paid: { label: "Part. payée",  cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  paid:           { label: "Payée",        cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected:       { label: "Refusée",      cls: "bg-red-50 text-red-700 border-red-200" },
  draft:          { label: "Brouillon",    cls: "bg-slate-100 text-slate-700 border-slate-200" },
  cancelled:      { label: "Annulée",      cls: "bg-slate-100 text-slate-500 border-slate-200" },
};
const SOURCE_META = {
  manual:       { label: "Manuel",     cls: "bg-slate-50 text-slate-700 border-slate-200",     Icon: User },
  ocr:          { label: "OCR IA",     cls: "bg-violet-50 text-violet-700 border-violet-100",  Icon: ScanLine },
  import_batch: { label: "Import lot", cls: "bg-violet-50 text-violet-700 border-violet-100",  Icon: ScanLine },
  recurrence:   { label: "Récurrence", cls: "bg-indigo-50 text-indigo-700 border-indigo-100",  Icon: Repeat },
  axonaut:      { label: "Axonaut",    cls: "bg-blue-50 text-blue-700 border-blue-100",        Icon: Plug },
  pennylane:    { label: "Pennylane",  cls: "bg-emerald-50 text-emerald-700 border-emerald-100", Icon: Plug },
};

const PRESETS = [
  { key: "12m",  label: "12 mois glissants" },
  { key: "ytd",  label: "Année en cours" },
  { key: "90d",  label: "90 jours" },
  { key: "all",  label: "Tout l'historique" },
];
function presetRange(preset) {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  if (preset === "all") return { from: null, to: null };
  const d = new Date(now);
  if (preset === "90d") d.setDate(d.getDate() - 90);
  else if (preset === "ytd") return { from: `${now.getFullYear()}-01-01`, to };
  else /* 12m */ d.setMonth(d.getMonth() - 12);
  return { from: d.toISOString().slice(0, 10), to };
}

function KpiCard({ icon: Icon, label, value, count, tone, active, onClick, testId }) {
  const tones = {
    slate:  "border-slate-200 hover:border-slate-300 bg-white",
    emerald:"border-emerald-200 hover:border-emerald-300 bg-emerald-50/40",
    blue:   "border-blue-200 hover:border-blue-300 bg-blue-50/40",
    danger: "border-red-200 hover:border-red-300 bg-red-50/40",
  };
  const iconTone = {
    slate: "text-slate-600 bg-slate-100",
    emerald: "text-emerald-700 bg-emerald-100",
    blue: "text-blue-600 bg-blue-100",
    danger: "text-red-600 bg-red-100",
  };
  return (
    <button onClick={onClick} data-testid={testId}
      className={cn("w-full text-left rounded-xl border p-4 transition-all",
                    tones[tone],
                    active ? "ring-2 ring-offset-1 ring-blue-500 shadow-sm" : "hover:shadow-sm")}>
      <div className="flex items-center gap-2">
        <div className={cn("p-1.5 rounded-md", iconTone[tone])}>
          <Icon className="h-3.5 w-3.5" strokeWidth={2} />
        </div>
        <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tracking-tight text-slate-900">{value}</span>
        {typeof count === "number" && (
          <span className="text-[11px] text-slate-500">{count} facture{count > 1 ? "s" : ""}</span>
        )}
      </div>
    </button>
  );
}

function SortHead({ label, col, sortBy, sortDir, onSort, align = "left" }) {
  const active = sortBy === col;
  return (
    <TableHead className={cn("text-xs cursor-pointer select-none",
                             align === "right" && "text-right")}
               onClick={() => onSort(col)}>
      <div className={cn("inline-flex items-center gap-1",
                         align === "right" && "flex-row-reverse")}>
        <span className={active ? "text-slate-900" : ""}>{label}</span>
        {active
          ? sortDir === "asc"
            ? <ArrowUp className="h-3 w-3 text-blue-600" strokeWidth={2.5} />
            : <ArrowDown className="h-3 w-3 text-blue-600" strokeWidth={2.5} />
          : <ChevronDown className="h-3 w-3 opacity-30" strokeWidth={2} />}
      </div>
    </TableHead>
  );
}

export default function HistoriqueAchats({ embedded = false } = {}) {
  const [q, setQ] = useState("");
  const [preset, setPreset] = useState("12m");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState("invoice_date");
  const [sortDir, setSortDir] = useState("desc");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const r = presetRange(preset);
    setDateFrom(r.from); setDateTo(r.to); setPage(1);
  }, [preset]);

  const params = useMemo(() => ({
    q: q || undefined,
    status: status !== "all" ? status : undefined,
    source_kind: source !== "all" ? source : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    page, page_size: pageSize,
    sort_by: sortBy, sort_dir: sortDir,
  }), [q, status, source, dateFrom, dateTo, page, pageSize, sortBy, sortDir]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data: res } = await api.get("/supplier-invoices/list-enriched", { params });
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) toast.error(apiError(e));
      } finally { if (!cancelled) setLoading(false); }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [params]);

  const kpis = data?.kpis || {};
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const sortToggle = (col) => {
    if (sortBy === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const exportExcel = () => {
    if (!rows.length) return;
    const XLSX_rows = rows.map((r) => ({
      Date: r.invoice_date,
      "N° facture": r.invoice_number,
      Fournisseur: r.supplier_name,
      Statut: STATUS_META[r.status]?.label || r.status,
      Source: SOURCE_META[r.source_kind]?.label || r.source_kind,
      "Cat. PCG": r.pcg_code || "",
      "HT": r.total_ht, "TVA": r.total_vat, "TTC": r.total_ttc,
      "Payé": r.paid_amount_ttc, "Reste dû": r.remaining_ttc,
      "Retard (j)": r.days_overdue,
    }));
    const ws = XLSX.utils.json_to_sheet(XLSX_rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Historique achats");
    XLSX.writeFile(wb, `historique-achats-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`${XLSX_rows.length} lignes exportées`);
  };
  const downloadZip = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/purchases/files/export.zip`, "_blank");
  };

  return (
    <div className="space-y-5" data-testid="historique-achats-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          {!embedded && (
            <>
              <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Achats</div>
              <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
                  style={{ fontFamily: "Manrope, sans-serif" }}>
                Historique Achats
              </h1>
            </>
          )}
          <p className={embedded ? "text-sm text-slate-500" : "mt-1 text-sm text-slate-500"}>
            Reporting complet — export Excel et archive ZIP pour l'expert-comptable.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadZip}
                  data-testid="hist-archive-zip-btn">
            <Download className="h-4 w-4 mr-1.5" strokeWidth={2} /> Archive ZIP
          </Button>
          <Button onClick={exportExcel} disabled={!rows.length}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="hist-export-excel-btn">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" strokeWidth={2} /> Exporter Excel
          </Button>
        </div>
      </div>

      {/* KPIs cliquables */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={Wallet} label="Total période"
          value={eurCompact(kpis.total_all?.total_ttc || 0)}
          count={kpis.total_all?.count || 0}
          tone="slate" active={status === "all"}
          onClick={() => { setStatus("all"); setPage(1); }}
          testId="kpi-hist-total" />
        <KpiCard icon={CheckCircle2} label="Payées"
          value={eurCompact((kpis.total_all?.total_ttc || 0) - (kpis.outstanding?.total_ttc || 0))}
          count={(kpis.total_all?.count || 0) - (kpis.outstanding?.count || 0)}
          tone="emerald" active={status === "paid"}
          onClick={() => { setStatus(status === "paid" ? "all" : "paid"); setPage(1); }}
          testId="kpi-hist-paid" />
        <KpiCard icon={FileText} label="Encours"
          value={eurCompact(kpis.outstanding?.total_ttc || 0)}
          count={kpis.outstanding?.count || 0}
          tone="blue" active={status === "validated"}
          onClick={() => { setStatus(status === "validated" ? "all" : "validated"); setPage(1); }}
          testId="kpi-hist-outstanding" />
        <KpiCard icon={AlertTriangle} label="En retard"
          value={eurCompact(kpis.overdue?.total_ttc || 0)}
          count={kpis.overdue?.count || 0}
          tone="danger" active={false}
          onClick={() => { setStatus("all"); setPage(1); }}
          testId="kpi-hist-overdue" />
      </div>

      {/* Filtres */}
      <Card className="border-slate-200 bg-white p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
                   placeholder="Recherche fournisseur, n° facture, montant…"
                   className="pl-8 h-9 text-sm" data-testid="hist-search" />
          </div>
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="hist-period">
              <CalendarDays className="h-3.5 w-3.5 mr-1 text-slate-500" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="hist-status">
              <SelectValue placeholder="Tous statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="to_validate">À valider</SelectItem>
              <SelectItem value="validated">Validée</SelectItem>
              <SelectItem value="imported">Importée</SelectItem>
              <SelectItem value="partially_paid">Part. payée</SelectItem>
              <SelectItem value="paid">Payée</SelectItem>
              <SelectItem value="rejected">Refusée</SelectItem>
            </SelectContent>
          </Select>
          <Select value={source} onValueChange={(v) => { setSource(v); setPage(1); }}>
            <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="hist-source">
              <SelectValue placeholder="Toutes sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes sources</SelectItem>
              <SelectItem value="manual">Manuel</SelectItem>
              <SelectItem value="ocr">OCR IA</SelectItem>
              <SelectItem value="import_batch">Import lot</SelectItem>
              <SelectItem value="recurrence">Récurrence</SelectItem>
              <SelectItem value="axonaut">Axonaut</SelectItem>
              <SelectItem value="pennylane">Pennylane</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table dense */}
      <Card className="border-slate-200 rounded-xl bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Chargement…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center" data-testid="hist-empty">
            <FileText className="h-8 w-8 mx-auto text-slate-300" strokeWidth={1.5} />
            <div className="mt-3 text-sm text-slate-600">Aucune facture sur cette période.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <SortHead label="Date" col="invoice_date" sortBy={sortBy} sortDir={sortDir} onSort={sortToggle} />
                  <SortHead label="N°" col="invoice_number" sortBy={sortBy} sortDir={sortDir} onSort={sortToggle} />
                  <SortHead label="Fournisseur" col="supplier_name" sortBy={sortBy} sortDir={sortDir} onSort={sortToggle} />
                  <TableHead className="text-xs">Source</TableHead>
                  <TableHead className="text-xs">Statut</TableHead>
                  <SortHead label="HT" col="total_ht" sortBy={sortBy} sortDir={sortDir} onSort={sortToggle} align="right" />
                  <TableHead className="text-xs text-right">TVA</TableHead>
                  <SortHead label="TTC" col="total_ttc" sortBy={sortBy} sortDir={sortDir} onSort={sortToggle} align="right" />
                  <SortHead label="Reste dû" col="remaining_ttc" sortBy={sortBy} sortDir={sortDir} onSort={sortToggle} align="right" />
                  <TableHead className="text-xs text-right w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const sm = STATUS_META[r.status] || { label: r.status, cls: "bg-slate-100" };
                  const src = SOURCE_META[r.source_kind] || SOURCE_META.manual;
                  const SrcIcon = src.Icon;
                  return (
                    <TableRow key={r.id} className="hover:bg-slate-50/70"
                              data-testid={`hist-row-${r.id}`}>
                      <TableCell className="text-slate-600 text-sm font-mono">{fmtDate(r.invoice_date)}</TableCell>
                      <TableCell className="text-slate-700 text-sm font-mono">{r.invoice_number || "—"}</TableCell>
                      <TableCell className="font-medium text-slate-900 text-sm truncate max-w-[220px]"
                                 title={r.supplier_name}>
                        {r.supplier_name || "—"}
                      </TableCell>
                      <TableCell>
                        <span className={cn("inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border whitespace-nowrap", src.cls)}>
                          <SrcIcon className="h-2.5 w-2.5" strokeWidth={2.5} />
                          {src.label}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn("text-[10px] font-medium", sm.cls)}>
                          {sm.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm text-slate-700 font-mono">{eur(r.total_ht)}</TableCell>
                      <TableCell className="text-right text-sm text-slate-500 font-mono">{eur(r.total_vat)}</TableCell>
                      <TableCell className="text-right text-sm text-slate-900 font-medium font-mono">{eur(r.total_ttc)}</TableCell>
                      <TableCell className="text-right text-sm font-mono">
                        {r.remaining_ttc > 0.01
                          ? <span className={cn(r.is_late ? "text-red-700 font-medium" : "text-slate-800")}>{eur(r.remaining_ttc)}</span>
                          : <span className="text-slate-400">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          <PdfPreviewPopover fileAssetId={r.file_asset_id}
                                              testId={`hist-pdf-${r.id}`} />
                          <Button variant="ghost" size="sm" asChild className="h-7 w-7 p-0">
                            <Link to={`/app/purchases/supplier-invoices/${r.id}`}
                                  data-testid={`hist-open-${r.id}`}>
                              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {!loading && total > 0 && (
          <div className="flex items-center justify-between gap-3 p-3 border-t border-slate-100 text-sm">
            <div className="text-slate-500">
              Affichage {(page - 1) * pageSize + 1}
              {" – "}{Math.min(page * pageSize, total)} sur {total}
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(pageSize)}
                      onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="w-[90px] h-8 text-xs" data-testid="hist-page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                  <SelectItem value="100">100 / page</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      data-testid="hist-page-prev">Précédent</Button>
              <span className="text-slate-600 text-xs px-1">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      data-testid="hist-page-next">Suivant</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
